// Monetisation → Revenue (contracts/revenue.md §4 overview, §5): the state now (MRR, ARR, active subscriptions
// and trials, billing issues, lifetime, paying customers), the range's totals, money / starts / churn per day,
// active subscriptions, trials and MRR over time, the trial funnel, breakdowns by product, storefront and
// paywall, and the charges expected in the next 30 days. Range, environment and store live in the URL.

import { Link } from "react-router";
import { useRevenueOverview } from "../../api/revenue";
import type { RevenueOverview } from "../../api/revenueSchemas";
import { BarChart, LineChart, StackedColumnChart, type BarDatum, type StackSeries } from "../../components/charts/charts";
import { tableStyles } from "../../components/table";
import { Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Stat } from "../../components/ui";
import { formatDateTime, formatShortDate } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import { fmtMoney, fmtUsd, productLabel, productShort, regionName, scopeText, triggerLabel } from "../../lib/revenue";
import pages from "../pages.module.css";
import { EstimateNote, MoneyCell, PlayerCell, RangeControls, ResetFilters, SandboxBadge, ScopeControls, scopeQuery, useRange, useScope } from "./components";
import r from "./revenue.module.css";

/** Entity colours, fixed: trials violet, paid teal, conversions blue; churn grey / orange / pink; money the accent. */
const COLOR = {
  trials: "var(--color-avatar-orbs-3-1)",
  paid: "var(--color-avatar-orbs-0-1)",
  conversions: "var(--color-avatar-orbs-1-1)",
  expirations: "var(--color-text-quaternary)",
  cancellations: "var(--color-avatar-orbs-2-1)",
  refunds: "var(--color-avatar-orbs-4-1)",
  money: "var(--color-accent)",
  fees: "var(--color-text-quaternary)",
};

const STARTS: StackSeries[] = [
  { key: "trials", label: "Trials", color: COLOR.trials },
  { key: "paid", label: "New paid", color: COLOR.paid },
  { key: "conversions", label: "Conversions", color: COLOR.conversions },
];
const CHURN: StackSeries[] = [
  { key: "expirations", label: "Expirations", color: COLOR.expirations },
  { key: "cancellations", label: "Cancellations", color: COLOR.cancellations },
  { key: "refunds", label: "Refunds", color: COLOR.refunds },
];
const MONEY: StackSeries[] = [
  { key: "proceeds", label: "Proceeds (est.)", color: COLOR.money },
  { key: "fees", label: "Store fees", color: COLOR.fees },
];

/** Dollars with cents, but round axis ticks without them. */
const usdAxis = (v: number) => fmtUsd(v, Number.isInteger(v));

export function RevenuePage() {
  const range = useRange();
  const scope = useScope();
  const q = useRevenueOverview({ from: range.from, to: range.to, env: scope.env, store: scope.store });
  const header = (
    <>
      <PageHeader
        title="Revenue"
        meta={
          <span>
            Subscriptions, money and churn, {formatShortDate(range.from)} – {formatShortDate(range.to)} (UTC) · {scopeText(scope.env, scope.store)}.
            Production and sandbox are counted apart unless you pick All.
          </span>
        }
        actions={
          <>
            <RangeControls range={range} />
            <ResetFilters />
          </>
        }
      />
      <div className={r.toolbar}>
        <ScopeControls scope={scope} />
        <span className={r.toolbarEnd}>
          <Link to={`/monetisation/customers${scopeQuery(scope)}`} className={pages.back}>
            Customers →
          </Link>
          <Link to={`/monetisation/events${scopeQuery(scope)}`} className={pages.back}>
            Events →
          </Link>
        </span>
      </div>
    </>
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={12} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  return (
    <>
      {header}
      <RevenueView data={q.data} fetching={q.isFetching} />
    </>
  );
}

function RevenueView({ data, fetching }: { data: RevenueOverview; fetching: boolean }) {
  const { now, totals, trialConversion: tc, funnel } = data;
  const labels = data.days.map((d) => formatShortDate(d.date));
  const resolved = tc.converted + tc.ended;
  const commission = data.estimates.appleCommission;

  const money = data.days.map((d) => ({
    key: d.date,
    label: formatShortDate(d.date),
    values: { proceeds: Math.max(0, d.proceedsUsd), fees: Math.max(0, d.revenueUsd - Math.max(0, d.proceedsUsd)) },
    notes: [`Revenue ${fmtUsd(d.revenueUsd)} (net)`, `${fmtInt(d.transactions)} transactions`, ...(d.refunds ? [`${fmtInt(d.refunds)} refunded`] : [])],
  }));
  const starts = data.days.map((d) => ({
    key: d.date,
    label: formatShortDate(d.date),
    values: { trials: d.newTrials, paid: d.newPaid, conversions: d.conversions },
    notes: d.renewals ? [`${fmtInt(d.renewals)} renewals`] : undefined,
  }));
  const churn = data.days.map((d) => ({ key: d.date, label: formatShortDate(d.date), values: { expirations: d.expirations, cancellations: d.cancellations, refunds: d.refunds } }));

  const products: BarDatum[] = data.byProduct.map((p) => ({
    key: p.productId ?? p.plan ?? "?",
    label: productLabel(p.productId, p.plan),
    value: p.revenueUsd,
    note: `${fmtInt(p.transactions)} charges · ${fmtInt(p.trials)} trials · ${fmtInt(p.active)} with Pro now`,
  }));
  const storefronts: BarDatum[] = data.byStorefront.slice(0, 12).map((s) => ({
    key: s.storefront ?? "unknown",
    label: s.storefront ? `${regionName(s.storefront) ?? s.storefront}` : "Unknown",
    value: s.revenueUsd,
    note: `${s.storefront ?? "no storefront"} · ${fmtInt(s.transactions)} charges · ${fmtInt(s.customers)} customers`,
  }));
  const triggers: BarDatum[] = data.byTrigger.map((t) => ({
    key: t.trigger ?? "unknown",
    label: triggerLabel(t.trigger),
    value: t.starts,
    note: `${fmtInt(t.trials)} with a trial · ${fmtUsd(t.revenueUsd)} so far`,
  }));
  const funnelRows: BarDatum[] = funnel
    ? [
        { key: "paywall", label: "Saw a paywall", value: funnel.paywallPlayers, note: "distinct players, allowed impressions" },
        { key: "trial", label: "Started a trial", value: funnel.trialStarters, note: `${fmtPct(funnel.paywallPlayers ? funnel.trialStarters / funnel.paywallPlayers : null, 1)} of paywall players` },
        { key: "converted", label: "Converted", value: funnel.converted, note: `${fmtPct(funnel.trialStarters ? funnel.converted / funnel.trialStarters : null, 1)} of trial starters` },
        { key: "paid", label: "Started paid", value: funnel.paidStarters, note: "bought without a trial (incl. lifetime)" },
      ]
    : [];
  const anyMoney = data.days.some((d) => d.revenueUsd !== 0);
  const anyStarts = data.days.some((d) => d.newTrials || d.newPaid || d.conversions);
  const anyChurn = data.days.some((d) => d.expirations || d.cancellations || d.refunds);
  const anyActive = data.days.some((d) => d.activeSubscriptions || d.activeTrials);

  return (
    <div aria-busy={fetching} data-testid="revenue">
      <div className={r.sectionHead}>
        <h2>Now</h2>
        <span className="monoLabel">{now.at ? `as of ${formatDateTime(now.at)}` : "current state"}</span>
      </div>
      <div className={pages.statStrip} data-testid="now-tiles">
        <Stat label="MRR" value={fmtUsd(now.mrrUsd)} sub="monthly equivalent, paid only" />
        <Stat label="ARR" value={fmtUsd(now.arrUsd)} sub="MRR × 12" />
        <Stat
          label="Active subscriptions"
          value={fmtInt(now.activeSubscriptions)}
          sub={`${fmtInt(now.activeByPlan.monthly ?? 0)} monthly · ${fmtInt(now.activeByPlan.annual ?? 0)} yearly`}
        />
        <Stat label="Active trials" value={fmtInt(now.activeTrials)} sub={`${fmtInt(now.trialsWillConvert)} will convert · ${fmtInt(now.trialsCancelled)} cancelled`} />
        <Stat label="Cancelled, still Pro" value={fmtInt(now.cancelledActive)} sub="auto-renew off until the period ends" />
        <Stat label="Billing issues" value={fmtInt(now.billingIssues)} sub="grace period + billing retry" />
        <Stat label="Lifetime" value={fmtInt(now.lifetime)} sub="owned, never renews" />
        <Stat label="Paying customers" value={fmtInt(now.payingCustomers)} sub={`of ${fmtInt(now.customers)} with a store purchase`} />
      </div>

      <div className={r.sectionHead}>
        <h2>
          {formatShortDate(data.from)} – {formatShortDate(data.to)}
        </h2>
        <span className="monoLabel">events in the range</span>
      </div>
      <div className={pages.statStrip} data-testid="range-totals">
        <Stat label="Revenue" value={fmtUsd(totals.revenueUsd)} sub="USD estimate, net of refunds" />
        <Stat label="Proceeds" value={fmtUsd(totals.proceedsUsd)} sub={`after Apple's ${fmtPct(commission)} and Stripe's fees`} />
        <Stat label="Transactions" value={fmtInt(totals.transactions)} sub="charges" />
        <Stat label="Refunds" value={fmtInt(totals.refunds)} sub={totals.refundedUsd ? `−${fmtUsd(totals.refundedUsd)}` : "none"} />
        <Stat label="New trials" value={fmtInt(totals.newTrials)} sub={`${fmtInt(totals.trialCancellations)} cancelled in the trial`} />
        <Stat label="Conversions" value={fmtInt(totals.conversions)} sub="trial → first charge" />
        <Stat
          label="Trial → paid"
          value={fmtPct(tc.rate, 1)}
          sub={resolved ? `${fmtInt(tc.converted)} of ${fmtInt(resolved)} resolved · ${fmtInt(tc.pending)} pending` : `none resolved · ${fmtInt(tc.pending)} pending`}
        />
        <Stat label="New paid" value={fmtInt(totals.newPaid)} sub={`incl. ${fmtInt(totals.lifetimePurchases)} lifetime`} />
        <Stat label="Renewals" value={fmtInt(totals.renewals)} sub={totals.reactivations ? `${fmtInt(totals.reactivations)} turned auto-renew back on` : "paid periods after the first"} />
        <Stat label="Cancellations" value={fmtInt(totals.cancellations)} sub="auto-renew off, after the trial" />
        <Stat label="Expirations" value={fmtInt(totals.expirations)} sub={`${fmtInt(totals.billingIssues)} billing issues`} />
        <Stat label="New customers" value={fmtInt(totals.newCustomers)} sub="first store subscription" />
      </div>

      <div className={r.grid}>
        <Card title="Revenue per day" className={r.wide} actions={<span className="mono">{fmtUsd(totals.revenueUsd)} net</span>}>
          {anyMoney ? (
            <StackedColumnChart rows={money} series={MONEY} label="Revenue per day: estimated proceeds and store fees (USD)" valueName="revenue" format={usdAxis} />
          ) : (
            <EmptyState title="No money in this range">No charges or refunds between these dates.</EmptyState>
          )}
          <ul className={r.netList} aria-label="Net by currency" data-testid="net-by-currency">
            {totals.revenue.length ? (
              [...totals.revenue]
                .sort((a, b) => b.amount - a.amount)
                .map((m) => (
                  <li key={m.currency} data-negative={m.amount < 0 || undefined}>
                    {fmtMoney(m)}
                  </li>
                ))
            ) : (
              <li>No charges in any currency</li>
            )}
          </ul>
        </Card>
        <Card title="Starts per day">
          {anyStarts ? (
            <StackedColumnChart rows={starts} series={STARTS} label="Starts per day: new trials, new paid subscriptions and conversions" valueName="starts" />
          ) : (
            <EmptyState title="No starts">No trial, purchase or conversion in this range.</EmptyState>
          )}
        </Card>
        <Card title="Churn per day">
          {anyChurn ? (
            <StackedColumnChart rows={churn} series={CHURN} label="Churn per day: expirations, cancellations and refunds" valueName="events" />
          ) : (
            <EmptyState title="No churn">Nobody cancelled, expired or was refunded in this range.</EmptyState>
          )}
        </Card>
        <Card title="Active subscriptions and trials">
          {anyActive ? (
            <LineChart
              labels={labels}
              series={[
                { key: "subscriptions", label: "Paid subscriptions", color: COLOR.paid, values: data.days.map((d) => d.activeSubscriptions) },
                { key: "trials", label: "Trials", color: COLOR.trials, values: data.days.map((d) => d.activeTrials) },
              ]}
              label="Active paid subscriptions and trials at each day's end"
            />
          ) : (
            <EmptyState title="Nothing active">No subscription or trial had access in this range.</EmptyState>
          )}
        </Card>
        <Card title="MRR" actions={<span className="mono">{fmtUsd(now.mrrUsd)} now</span>}>
          {data.days.some((d) => d.mrrUsd > 0) ? (
            <LineChart
              labels={labels}
              series={[{ key: "mrr", label: "MRR", color: COLOR.money, values: data.days.map((d) => d.mrrUsd), area: true }]}
              label="MRR (USD) at each day's end"
              format={usdAxis}
              integerTicks={false}
              testId="mrr-chart"
            />
          ) : (
            <EmptyState title="No MRR">No paid subscription had access in this range.</EmptyState>
          )}
        </Card>
        <Card title="Trial funnel" actions={<span className="monoLabel">players in the range</span>}>
          {funnel && funnelRows.some((x) => x.value > 0) ? (
            <div data-testid="funnel">
              <BarChart rows={funnelRows} label="Trial funnel: paywall players, trial starters, converted, paid starters" valueName="players" tone="violet" labelWidth={124} />
            </div>
          ) : (
            <EmptyState title="No funnel yet">The funnel fills once players see a paywall in this range.</EmptyState>
          )}
          <div className={r.facts} data-testid="trial-conversion">
            <Stat label="Trials started" value={fmtInt(tc.started)} />
            <Stat label="Converted" value={fmtInt(tc.converted)} />
            <Stat label="Ended unpaid" value={fmtInt(tc.ended)} />
            <Stat label="Pending" value={fmtInt(tc.pending)} />
          </div>
        </Card>
        <Card title="By product" actions={<span className="monoLabel">revenue (USD est.)</span>}>
          {products.length ? (
            <BarChart rows={products} label="Revenue by product (USD estimate)" valueName="revenue" tone="accent" format={usdAxis} labelWidth={132} integerTicks={false} />
          ) : (
            <EmptyState title="No products">Nothing was bought or started in this range.</EmptyState>
          )}
        </Card>
        <Card title="By storefront" actions={<span className="monoLabel">revenue (USD est.)</span>}>
          {storefronts.length ? (
            <BarChart rows={storefronts} label="Revenue by storefront (USD estimate)" valueName="revenue" tone="accent" format={usdAxis} labelWidth={124} integerTicks={false} />
          ) : (
            <EmptyState title="No storefronts">No store activity in this range.</EmptyState>
          )}
        </Card>
        <Card title="By paywall" actions={<span className="monoLabel">subscriptions started</span>} className={r.wide}>
          {triggers.length ? (
            <BarChart rows={triggers} label="Subscriptions started by the paywall they came from" valueName="started" labelWidth={110} />
          ) : (
            <EmptyState title="No starts">No subscription started in this range.</EmptyState>
          )}
        </Card>
        <UpcomingCard data={data} />
      </div>
      <EstimateNote />
    </div>
  );
}

function UpcomingCard({ data }: { data: RevenueOverview }) {
  const total = data.upcoming.reduce((a, u) => a + (u.usd ?? 0), 0);
  return (
    <Card
      title="Upcoming charges"
      className={r.wide}
      padded={false}
      actions={<span className="monoLabel">next 30 days · {data.upcoming.length ? `≈ ${fmtUsd(total)}` : "none"}</span>}
    >
      {data.upcoming.length === 0 ? (
        <EmptyState title="Nothing expected">No trial converts and no subscription renews in the next 30 days.</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid="upcoming">
            <thead>
              <tr>
                <th>When</th>
                <th>Customer</th>
                <th>Product</th>
                <th data-align="right">Price</th>
                <th>Charge</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming.map((u, i) => (
                <tr key={`${u.subscriptionId ?? i}-${u.at}`} data-testid="upcoming-row">
                  <td className="mono">{formatDateTime(u.at)}</td>
                  <td>
                    <PlayerCell userId={u.userId} handle={u.handle} />
                  </td>
                  <td>
                    {productShort(u.productId, u.plan)} <SandboxBadge env={u.environment} />
                  </td>
                  <td data-align="right">
                    <MoneyCell price={u.price} usd={u.usd} align="right" />
                  </td>
                  <td>{u.kind === "conversion" ? <Chip tone="violet">trial converts</Chip> : <Chip tone="success">renewal</Chip>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
